import React, { useContext, useEffect, useMemo } from 'react';
import cx from 'classnames';
import RecorderDrawerContext from '../../Context';
import useMessageTemplates from '~/react-hooks/use-message-templates';
import {
  DropdownSelector,
  SelectorType,
} from '@vimily/bonjoro-ui-components-v2/DropdownSelector';
import { DropdownAlignment } from '@vimily/bonjoro-ui-components-v2/Dropdown';
import { MessageTemplateType } from '~/types/models';
import { useFeatures } from '~/react-hooks/use-features';
import { TemplateState } from '~/App/Modules/Settings/MessageTemplates/Pages/Editor';
import { MessageTemplateRow } from './MessageTemplateRow';

const defaultTemplateName =
  window.Bonjoro?.messageTemplate?.defaultCtaText ?? 'Send your own videos';

export type MessageTemplatePanelProps = {
  className?: string;
};

export function MessageTemplatePanel({ className }: MessageTemplatePanelProps) {
  const { canCustomiseLandingPage, canAccessNextGenTemplate } = useFeatures();
  const {
    messageTemplate,
    hasValidVideo,
    setInfoModalOpen,
    setMessageTemplate,
    setMessage,
    demoTemplateAsDefault,
    fixedTemplateDropdown,
    onMessageTemplateSelectionClick,
  } = useContext(RecorderDrawerContext);

  const { messageTemplates } = useMessageTemplates({
    onTemplatesLoaded: ({ data }) => {
      if (!canAccessNextGenTemplate) {
        // The active template set is the assigned template on the greet object and it might not be using a postcard layout
        // since message templates are already filtered if they have no access to next gen templates
        // we get the first message template and set that as the active template
        const [template] = data;
        setMessageTemplate(template);
        return;
      }
    },
  });

  useEffect(() => {
    if (!messageTemplate) return;

    setMessage(messageTemplate?.email_template?.message_template ?? '');
  }, [messageTemplate]);

  const items = useMemo(() => {
    if (!messageTemplates?.data) return {};

    return messageTemplates?.data?.reduce((items, messageTemplate) => {
      const name = messageTemplate.name ?? defaultTemplateName;
      return {
        ...items,
        [messageTemplate.id]: (
          <MessageTemplateRow
            state={messageTemplate.state}
            templateType={messageTemplate.template_type}
            name={!canCustomiseLandingPage ? 'Bonjoro branded template' : name}
          />
        ),
      };
    }, {}) as Record<string, React.ReactNode>;
  }, [messageTemplates]);

  const onTemplateSelect = (selected: string[]) => {
    if (!messageTemplates?.data) return;
    const messageTemplate = messageTemplates?.data.filter((template) =>
      selected.includes(`${template.id}`)
    )[0];
    setMessageTemplate(messageTemplate);

    onMessageTemplateSelectionClick?.(messageTemplate);
  };

  const showPlaceholderAsSelected = useMemo(() => {
    if (!messageTemplates?.data) return false;
    const templates = messageTemplates?.data.filter(
      (template) => template.id === messageTemplate?.id
    );
    return templates.length == 0;
  }, [messageTemplates, messageTemplate]);

  if (!messageTemplates) return <></>;

  return (
    <div className={cx('flex flex-col gap-y-4 w-full relative', className)}>
      <div className='flex flex-row justify-between'>
        <div className='flex flex-row gap-x-4 items-center'>
          <i className='icon icon-templates !v2-bg-neutral-900' />
          <span className='text-[21px] font-bold'>Message template</span>
        </div>
        <button
          className={cx([
            'flex flex-row v2-group cursor-pointer gap-x-4 border-none bg-transparent',
            'underline v2-text-neutral-500 hover:v2-text-neutral-900 text-base font-thin',
          ])}
          onClick={() => setInfoModalOpen(true)}
        >
          What is this?
        </button>
      </div>
      <DropdownSelector
        dropdownProps={{
          containerAlignment: DropdownAlignment.Start,
          containerClassName: cx('!w-full !z-10', {
            '!bottom-9': !fixedTemplateDropdown && !hasValidVideo,
          }),
          id: 'message-template-dropdown',
          width: 500,
        }}
        showBodyPlaceholderAsSelected={showPlaceholderAsSelected}
        bodyPlaceholder={
          demoTemplateAsDefault ? (
            <MessageTemplateRow
              state={TemplateState.Demo as string}
              templateType={MessageTemplateType.Postcard}
              name={defaultTemplateName}
            />
          ) : (
            'Select a message template'
          )
        }
        disabled={!canCustomiseLandingPage}
        bodyContainerClassName='!w-full v2-border-neutral-300 v2-border-[1px]'
        type={SelectorType.Select}
        items={items}
        selected={messageTemplate?.id ? [`${messageTemplate?.id}`] : undefined}
        onSelect={onTemplateSelect}
      />
    </div>
  );
}
